/**
 * Body State Page - Third onboarding page (Step 3 of 4).
 * Feature 016: Conversational onboarding flow for wellness baseline.
 *
 * User Story 4: "Your body starting point 💪"
 * Collects: cycle_status, activity_level, sleep_quality_baseline
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../hooks/useOnboarding';
import ProgressIndicator from '../../components/onboarding/ProgressIndicator';

// T050: Cycle status options
const CYCLE_OPTIONS = [
  { value: 'regular', label: 'Regular', emoji: '🔴' },
  { value: 'irregular', label: 'Irregular', emoji: '🌀' },
  { value: 'stopped_menopause', label: 'Stopped (Menopause)', emoji: '🛑' }
];

// T052: Activity level options (2x2 grid)
const ACTIVITY_OPTIONS = [
  { value: 'sedentary', label: 'Sedentary', emoji: '🛋️' },
  { value: 'lightly_active', label: 'Lightly active', emoji: '🚶' },
  { value: 'moderately_active', label: 'Moderately active', emoji: '🏃' },
  { value: 'very_active', label: 'Very active', emoji: '💪' }
];

// T054: Sleep quality options
const SLEEP_OPTIONS = [
  { value: 'poor', label: 'Poor', emoji: '😴' },
  { value: 'okay', label: 'Okay', emoji: '😐' },
  { value: 'good', label: 'Good', emoji: '😊' }
];

const BodyStatePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { savePage, isSaving } = useOnboarding();

  // Form state
  const [cycleStatus, setCycleStatus] = useState('');
  const [activityLevel, setActivityLevel] = useState('');
  const [sleepQuality, setSleepQuality] = useState('');

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Pre-fill existing data if user returns to this page
  useEffect(() => {
    if (user?.profile?.wellness_baseline) {
      const { cycle_status, activity_level, sleep_quality_baseline } = user.profile.wellness_baseline;

      if (cycle_status) setCycleStatus(cycle_status);
      if (activity_level) setActivityLevel(activity_level);
      if (sleep_quality_baseline) setSleepQuality(sleep_quality_baseline);
    }
  }, [user]);

  // Validate form
  const validateForm = (): boolean => {
    const validationErrors: Record<string, string> = {};

    if (!cycleStatus) {
      validationErrors.cycleStatus = 'Please select your cycle status';
    }

    if (!activityLevel) {
      validationErrors.activityLevel = 'Please select your activity level';
    }

    if (!sleepQuality) {
      validationErrors.sleepQuality = 'Please select your sleep quality';
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // T057: Handle form submission
  const handleNext = async () => {
    if (!validateForm()) {
      return;
    }

    // Prepare data for API
    const pageData = {
      cycle_status: cycleStatus,
      activity_level: activityLevel,
      sleep_quality_baseline: sleepQuality
    };

    try {
      // Save body state data via API
      await savePage.mutateAsync({ page: 'body_state', data: pageData });

      // Success toast
      setToastType('success');
      setToastMessage('Body state saved successfully!');
      setShowToast(true);

      // T058: Navigate to next page
      setTimeout(() => {
        navigate('/onboarding/struggles');
      }, 1000);
    } catch (err: any) {
      // Error handling
      setToastType('error');
      setToastMessage(err.message || 'Failed to save body state. Please try again.');
      setShowToast(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 px-4 py-12">
      <div className="max-w-2xl w-full animate-fade-in">
        {/* T049: Progress Indicator - Step 3 of 4, 75% */}
        <ProgressIndicator currentStep={3} totalSteps={4} />

        <div className="card shadow-elegant mt-8">
          {/* T048: Page header with emoticon */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-serif font-bold text-primary-800 mb-3">
              Your body starting point 💪
            </h1>
            <p className="text-base text-neutral-600">
              This helps BOSI understand your current state so we can guide effectively
            </p>
          </div>

          {/* Toast notification with aria-live for screen readers (T086) */}
          {showToast && (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className={`mb-6 p-4 border-l-4 rounded-r-lg animate-slide-down ${
                toastType === 'success'
                  ? 'bg-green-50 border-green-400'
                  : 'bg-red-50 border-red-400'
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  toastType === 'success' ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {toastMessage}
              </p>
            </div>
          )}

          <div className="space-y-8">
            {/* T050-T051: Cycle status question */}
            <div>
              <label className="block text-base font-medium text-neutral-800 mb-2">
                Are your cycles still ongoing?
              </label>
              <p className="text-sm text-neutral-500 italic mb-3">
                Your hormones affect fat loss, energy and recovery
              </p>

              {/* 3 cycle options in horizontal row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {CYCLE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setCycleStatus(option.value)}
                    className={`p-4 rounded-lg border-2 text-center font-medium transition-all duration-200 ${
                      cycleStatus === option.value
                        ? 'border-primary-600 bg-primary-50 shadow-md text-primary-800'
                        : 'border-primary-200 bg-white hover:border-primary-400 text-neutral-700'
                    }`}
                  >
                    {/* T056: Emoticon above label */}
                    <div className="text-2xl mb-2">{option.emoji}</div>
                    <div className="text-sm">{option.label}</div>
                  </button>
                ))}
              </div>

              {errors.cycleStatus && (
                <p className="text-sm text-red-600 mt-2">{errors.cycleStatus}</p>
              )}
            </div>

            {/* T052-T053: Activity level question (2x2 grid) */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-base font-medium text-neutral-800 mb-2">
                How active are you currently?
              </label>
              <p className="text-sm text-neutral-500 italic mb-3">
                So we can recommend the right intensity for you
              </p>

              {/* 2x2 grid on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ACTIVITY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setActivityLevel(option.value)}
                    className={`p-4 rounded-lg border-2 text-center font-medium transition-all duration-200 ${
                      activityLevel === option.value
                        ? 'border-primary-600 bg-primary-50 shadow-md text-primary-800'
                        : 'border-primary-200 bg-white hover:border-primary-400 text-neutral-700'
                    }`}
                  >
                    {/* T056: Emoticon above label */}
                    <div className="text-2xl mb-2">{option.emoji}</div>
                    <div className="text-sm">{option.label}</div>
                  </button>
                ))}
              </div>

              {errors.activityLevel && (
                <p className="text-sm text-red-600 mt-2">{errors.activityLevel}</p>
              )}
            </div>

            {/* T054-T055: Sleep quality question */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-base font-medium text-neutral-800 mb-2">
                On average, how is your sleep?
              </label>
              <p className="text-sm text-neutral-500 italic mb-3">
                Sleep directly affects fat loss and energy
              </p>

              {/* 3 sleep options in horizontal row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {SLEEP_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSleepQuality(option.value)}
                    className={`p-4 rounded-lg border-2 text-center font-medium transition-all duration-200 ${
                      sleepQuality === option.value
                        ? 'border-primary-600 bg-primary-50 shadow-md text-primary-800'
                        : 'border-primary-200 bg-white hover:border-primary-400 text-neutral-700'
                    }`}
                  >
                    {/* T056: Emoticon above label */}
                    <div className="text-2xl mb-2">{option.emoji}</div>
                    <div className="text-sm">{option.label}</div>
                  </button>
                ))}
              </div>

              {errors.sleepQuality && (
                <p className="text-sm text-red-600 mt-2">{errors.sleepQuality}</p>
              )}
            </div>

            {/* Submit button */}
            <button
              type="button"
              onClick={handleNext}
              disabled={isSaving}
              className="btn btn-primary w-full text-lg py-4 mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Next Step →'}
            </button>
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-6 text-xs text-center text-neutral-600">
          This baseline helps us personalize your daily guidance
        </p>
      </div>
    </div>
  );
};

export default BodyStatePage;
